/**
 * APX Advisors — Privacy Policy Page
 * Publicly accessible at /privacy-policy (no auth required)
 * Uses inline styles to be fully independent of app theme/CSS variables
 */
import { useEffect } from "react";

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#ffffff",
    color: "#111827",
    fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
  } as React.CSSProperties,
  header: {
    backgroundColor: "#0f172a",
    padding: "20px 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  } as React.CSSProperties,
  headerLogo: {
    width: "32px",
    height: "32px",
    borderRadius: "6px",
    backgroundColor: "#b8902a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "14px",
    flexShrink: 0,
  } as React.CSSProperties,
  headerTitle: {
    color: "#ffffff",
    fontSize: "15px",
    fontWeight: 600,
    margin: 0,
  } as React.CSSProperties,
  headerSub: {
    color: "#94a3b8",
    fontSize: "12px",
    margin: 0,
  } as React.CSSProperties,
  container: {
    maxWidth: "760px",
    margin: "0 auto",
    padding: "48px 24px 80px",
  } as React.CSSProperties,
  h1: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#0f172a",
    margin: "0 0 6px",
  } as React.CSSProperties,
  lastUpdated: {
    fontSize: "13px",
    color: "#6b7280",
    margin: "0 0 40px",
  } as React.CSSProperties,
  section: {
    marginBottom: "36px",
  } as React.CSSProperties,
  h2: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#0f172a",
    margin: "0 0 10px",
    paddingBottom: "6px",
    borderBottom: "1px solid #e5e7eb",
  } as React.CSSProperties,
  h3: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#1e293b",
    margin: "16px 0 6px",
  } as React.CSSProperties,
  p: {
    fontSize: "14px",
    lineHeight: "1.7",
    color: "#374151",
    margin: "0 0 10px",
  } as React.CSSProperties,
  ul: {
    paddingLeft: "20px",
    margin: "8px 0",
    fontSize: "14px",
    lineHeight: "1.7",
    color: "#374151",
  } as React.CSSProperties,
  li: {
    marginBottom: "4px",
  } as React.CSSProperties,
  link: {
    color: "#1d4ed8",
    textDecoration: "underline",
  } as React.CSSProperties,
  strong: {
    fontWeight: 600,
    color: "#111827",
  } as React.CSSProperties,
  smsBox: {
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "16px 20px",
    marginTop: "12px",
  } as React.CSSProperties,
  footer: {
    borderTop: "1px solid #e5e7eb",
    marginTop: "48px",
    paddingTop: "24px",
    fontSize: "13px",
    color: "#9ca3af",
    textAlign: "center",
  } as React.CSSProperties,
};

export default function PrivacyPolicy() {
  // Enable scrolling on this page — the global CSS sets body overflow:hidden for the dashboard
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "auto";
    document.documentElement.style.overflow = "auto";
    return () => {
      document.body.style.overflow = prev;
      document.documentElement.style.overflow = "";
    };
  }, []);

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLogo}>A</div>
        <div>
          <p style={styles.headerTitle}>APX Advisors LLC</p>
          <p style={styles.headerSub}>apxcfo.com · (310) 626-2670</p>
        </div>
      </div>

      {/* Content */}
      <div style={styles.container}>
        <h1 style={styles.h1}>Privacy Policy</h1>
        <p style={styles.lastUpdated}>Last updated: May 26, 2026</p>

        {/* 1. Introduction */}
        <div style={styles.section}>
          <h2 style={styles.h2}>1. Introduction</h2>
          <p style={styles.p}>
            APX Advisors LLC ("APX," "we," "our," or "us") is committed to protecting your personal
            information. This Privacy Policy explains how we collect, use, disclose, and safeguard
            your information when you interact with our services, internal operations platform, or
            any communications we send on your behalf, including SMS text messages.
          </p>
        </div>

        {/* 2. Information We Collect */}
        <div style={styles.section}>
          <h2 style={styles.h2}>2. Information We Collect</h2>
          <p style={styles.p}>We may collect the following types of information:</p>
          <ul style={styles.ul}>
            <li style={styles.li}>Name, email address, phone number, and business information you provide to us</li>
            <li style={styles.li}>Financial and bookkeeping data shared as part of our advisory and bookkeeping services</li>
            <li style={styles.li}>Communication records, including SMS messages sent through our platform</li>
            <li style={styles.li}>Usage data and analytics related to your interactions with our services</li>
          </ul>
        </div>

        {/* 3. SMS Communications */}
        <div style={styles.section}>
          <h2 style={styles.h2}>3. SMS / Text Message Communications</h2>

          <div style={styles.smsBox}>
            <h3 style={{ ...styles.h3, marginTop: 0 }}>3.1 Program Description</h3>
            <p style={styles.p}>
              APX Advisors LLC operates an SMS messaging program to communicate with existing clients
              who have provided express written consent to receive text messages. Messages sent through
              this program include, but are not limited to: bookkeeping status notifications, document
              requests, appointment reminders, invoice and payment notices, tax filing updates, and
              general account communications related to the services described in the client's
              engagement agreement with APX.
            </p>
            <p style={styles.p}>
              Messages are sent from the phone number <strong style={styles.strong}>(424) 678-1277</strong>, operated by APX
              through Twilio, Inc. as our SMS service provider. Standard message and data rates may
              apply depending on your mobile carrier and plan.
            </p>

            <h3 style={styles.h3}>3.2 How We Obtain Consent</h3>
            <p style={styles.p}>
              APX collects express written consent to send SMS messages through the client engagement
              agreement and contact forms. Consent is obtained via an optional, clearly labeled
              checkbox — providing your phone number, email address, or checking the consent box is
              entirely voluntary and is <strong style={styles.strong}>not required</strong> to receive
              services from APX Advisors LLC.
            </p>
            <p style={styles.p}>
              The consent statement reads as follows:
            </p>
            <div style={{ backgroundColor: "#f1f5f9", border: "1px solid #cbd5e1", borderRadius: "6px", padding: "14px 16px", margin: "0 0 12px" }}>
              <p style={{ ...styles.p, margin: 0, fontStyle: "italic" }}>
                ☐ I agree to receive text messages (SMS) from APX Advisors LLC / APXsystem.io at the
                phone number provided above regarding my account, appointments, bookkeeping, tax, and
                advisory services. Message frequency may vary and message &amp; data rates may apply.
                Reply STOP to opt out or HELP for assistance. I have reviewed the{" "}
                <a href="/terms" style={styles.link} target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>
                {" "}and{" "}
                <a href="/privacy-policy" style={styles.link} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </p>
            </div>
            <p style={styles.p}>
              Consent is voluntary and is not a condition of purchasing any service. Clients who
              prefer not to receive SMS communications may indicate this preference at any time
              without affecting their service.
            </p>

            <h3 style={styles.h3}>3.3 Types of Messages Sent</h3>
            <p style={styles.p}>
              APX does not send promotional or marketing messages through this program. All messages
              are transactional and directly related to services the client has engaged APX to perform.
              Examples include:
            </p>
              <ul style={styles.ul}>
                  <li style={styles.li}>"Your March financial reports is ready for review."</li>
                  <li style={styles.li}>"We need your Q3 bank statements to complete your filing."</li>
                  <li style={styles.li}>"Your tax appointment is scheduled for tomorrow at 2:00 PM."</li>
                  <li style={styles.li}>"Invoice #1042 for $450 is due on April 20."</li>
                  <li style={styles.li}>"Your Xero file has been reconciled through March 31."</li>
                </ul>

            <h3 style={styles.h3}>3.4 Opt-Out Instructions</h3>
            <p style={styles.p}>
              You may opt out of receiving SMS messages from APX at any time by replying{" "}
              <strong style={styles.strong}>STOP</strong> to any message you receive from us. Upon receipt of a STOP
              message, APX will send a single confirmation message and immediately cease all further
              SMS communications to that number. You may also opt out by contacting us directly at{" "}
              <a href="mailto:privacy@apxcfo.com" style={styles.link}>privacy@apxcfo.com</a>{" "}
              or (310) 626-2670. To resume receiving messages after opting out, reply{" "}
              <strong style={styles.strong}>START</strong> to the same number or contact us directly.
            </p>

            <h3 style={styles.h3}>3.5 Help</h3>
            <p style={styles.p}>
              If you need assistance regarding our SMS program, reply <strong style={styles.strong}>HELP</strong> to any
              message from APX or contact us at{" "}
              <a href="mailto:privacy@apxcfo.com" style={styles.link}>privacy@apxcfo.com</a>{" "}
              or (310) 626-2670. We will respond within one business day.
            </p>

            <h3 style={styles.h3}>3.6 SMS Data Handling</h3>
            <p style={styles.p}>
              APX does not sell, rent, share, or disclose your mobile phone number or SMS consent
              status to any third party for marketing or promotional purposes. Your phone number is
              used exclusively to deliver the transactional messages described in Section 3.3 above.
            </p>
            <p style={styles.p}>
              APX uses <strong style={styles.strong}>Twilio, Inc.</strong> as its SMS delivery provider. Twilio processes
              message content and delivery metadata (such as delivery status and timestamps) on APX's
              behalf as a data processor. Twilio is contractually prohibited from using this data for
              any purpose other than delivering messages on APX's behalf. You may review Twilio's
              privacy practices at{" "}
              <a
                href="https://www.twilio.com/en-us/legal/privacy"
                style={styles.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                twilio.com/en-us/legal/privacy
              </a>.
            </p>
            <p style={styles.p}>
              Mobile phone numbers and SMS consent records are retained for the duration of the client
              engagement and for a reasonable period thereafter for record-keeping and compliance
              purposes. You may request deletion of your SMS consent record at any time by contacting
              us at <a href="mailto:privacy@apxcfo.com" style={styles.link}>privacy@apxcfo.com</a>.
            </p>

            <h3 style={styles.h3}>3.7 Carrier Disclaimer</h3>
            <p style={{ ...styles.p, marginBottom: 0 }}>
              Message and data rates may apply. Message frequency varies based on account activity
              and service level. For questions about your mobile plan, contact your wireless carrier.
            </p>
          </div>
        </div>

        {/* 4. How We Use Your Information */}
        <div style={styles.section}>
          <h2 style={styles.h2}>4. How We Use Your Information</h2>
          <ul style={styles.ul}>
            <li style={styles.li}>To deliver bookkeeping, advisory, and financial services to your business</li>
            <li style={styles.li}>To send account updates, reminders, and notifications via SMS or email</li>
            <li style={styles.li}>To improve our services and internal operations</li>
            <li style={styles.li}>To comply with legal and regulatory obligations</li>
          </ul>
        </div>

        {/* 5. Data Security */}
        <div style={styles.section}>
          <h2 style={styles.h2}>5. Data Security</h2>
          <p style={styles.p}>
            APX Advisors is a certified Xero developer partner. We implement industry-standard
            security measures to protect your data, including encrypted data transmission and
            access controls. Client financial data is handled in accordance with applicable
            accounting and data protection standards.
          </p>
        </div>

        {/* 6. Sharing of Information */}
        <div style={styles.section}>
          <h2 style={styles.h2}>6. Sharing of Information</h2>
          <p style={styles.p}>
            We do not sell or rent your personal information to third parties. We may share
            information with trusted service providers (such as Twilio for SMS delivery and
            Xero for accounting) solely to deliver our services, subject to confidentiality
            obligations. We do not share your mobile phone number or SMS consent data with any
            third party for marketing purposes.
          </p>
        </div>

        {/* 7. Your Rights */}
        <div style={styles.section}>
          <h2 style={styles.h2}>7. Your Rights</h2>
          <p style={styles.p}>
            You have the right to access, correct, or request deletion of your personal information.
            California residents have additional rights under the CCPA/CPRA, including the right to
            know, delete, correct, and opt out of the sale of personal information. APX does not sell
            personal information. To exercise any of these rights, contact us at{" "}
            <a href="mailto:privacy@apxcfo.com" style={styles.link}>privacy@apxcfo.com</a>.
          </p>
        </div>

        {/* 8. Contact Us */}
        <div style={styles.section}>
          <h2 style={styles.h2}>8. Contact Us</h2>
          <p style={styles.p}>
            APX Advisors LLC<br />
            1225 W 190th St, Los Angeles, CA 90248<br />
            Phone: <a href="tel:+13106262670" style={styles.link}>(310) 626-2670</a><br />
            Email: <a href="mailto:privacy@apxcfo.com" style={styles.link}>privacy@apxcfo.com</a><br />
            Website: <a href="https://apxcfo.com" style={styles.link} target="_blank" rel="noopener noreferrer">apxcfo.com</a>
          </p>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <p style={{ margin: 0 }}>
            © {new Date().getFullYear()} APX Advisors LLC · All rights reserved ·{" "}
            <a href="mailto:privacy@apxcfo.com" style={{ ...styles.link, color: "#9ca3af" }}>privacy@apxcfo.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}
